import { Trash2 } from 'lucide-react';
import { IconButton } from '../ui';
import { CancelIcon, ConfirmIcon, SendIcon } from './icons';
import styles from './ComposerPill.module.css';

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// Figma "Voice Recording" (14701:126431 / L 14701:126554): a left-packed cluster of thin bars driven
// by the live mic level (see use-dictation `levels`), with the timer pushed to the track's right edge.
// Two sizes — the compact floating panel keeps the design's small thin bars; the full-size chat scales
// them up so the waveform reads on the wider composer.
type WaveSize = { count: number; barW: number; gap: number; band: number; min: number; max: number };
// Small/compact chat keeps the thinnest bars; the full-size chat scales them up.
const COMPACT: WaveSize = { count: 44, barW: 1, gap: 1.5, band: 16, min: 3, max: 14 };
const FULL: WaveSize = { count: 56, barW: 2.5, gap: 2, band: 26, min: 4, max: 22 };

export function RecordingBar({
  seconds,
  levels,
  cancelLabel,
  sendLabel,
  onCancel,
  onConfirm,
  full = false,
  variant = 'dictation',
  busy = false,
}: {
  seconds: number;
  levels: number[];
  cancelLabel: string;
  sendLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
  // The full-size chat scales the waveform up; the compact panel keeps the small design bars.
  full?: boolean;
  // Which recording this bar is driving. `dictation` transcribes into the field, so leaving it is
  // a plain cancel (✗). `voiceNote` is CAPTURED AUDIO about to be sent, so it gets the phones'
  // recorder row instead: a destructive trash (the take is thrown away, not just dismissed) and
  // the live red dot that says the mic is recording. Everything else is identical, deliberately —
  // one bar, so the two can't drift apart. See `ComposerVoiceRecorderRow` on both phones.
  variant?: 'dictation' | 'voiceNote';
  // The clip is uploading: the bar stays put and both controls go inert, so a second tap can't
  // send it twice or discard a take that is already on its way.
  busy?: boolean;
}) {
  const s = full ? FULL : COMPACT;
  const voiceNote = variant === 'voiceNote';
  // Newest levels, right-most, packed into a fixed cluster (front-padded with silence).
  const padded = [...Array(Math.max(0, s.count - levels.length)).fill(0), ...levels.slice(-s.count)];
  return (
    <>
      <IconButton
        size="small"
        variant="text"
        // Throwing a recorded take away is destructive in a way dismissing a dictation is not.
        color={voiceNote ? 'destructive' : 'standard'}
        onClick={onCancel}
        disabled={busy}
        aria-label={cancelLabel}
        title={cancelLabel}
        className={styles.chatRound}
      >
        {voiceNote ? <Trash2 size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden /> : <CancelIcon />}
      </IconButton>
      <div
        style={{
          flex: 1,
          minWidth: 0,
          height: full ? 36 : 30,
          display: 'flex',
          alignItems: 'center',
          // Waveform hugs the left; the timer is pushed to the track's right edge.
          justifyContent: 'space-between',
          gap: 12,
          padding: '0 14px',
          borderRadius: 999,
          background: 'var(--ds-surface-standard-bg)',
        }}
      >
        <div style={{ flexShrink: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          {voiceNote && (
            // The live red dot both phones' recorder row draws: audio is being CAPTURED here, not
            // transcribed away. `orion-mic-live` is the shared pulse (and stills under
            // prefers-reduced-motion).
            <span
              aria-hidden
              className="orion-mic-live"
              style={{ flexShrink: 0, width: 8, height: 8, borderRadius: '50%', background: 'var(--ds-fill-destructive-default)' }}
            />
          )}
          <div aria-hidden style={{ flexShrink: 1, minWidth: 0, height: s.band, display: 'flex', alignItems: 'center', gap: s.gap, overflow: 'hidden' }}>
            {padded.map((lvl, i) => (
              <span
                key={i}
                // No canned animation — the height IS the live mic level, so the bars react to the voice.
                style={{
                  flexShrink: 0,
                  width: s.barW,
                  height: Math.min(s.max, Math.max(s.min, lvl * s.max)),
                  borderRadius: s.barW / 2,
                  background: 'var(--ds-text-primary)',
                  transition: 'height 90ms linear',
                }}
              />
            ))}
          </div>
        </div>
        <span style={{ flexShrink: 0, fontSize: 12, fontWeight: 400, color: 'var(--ds-text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
          {formatElapsed(seconds)}
        </span>
      </div>
      <IconButton
        size="small"
        variant="contained"
        color="standard"
        onClick={onConfirm}
        loading={busy}
        aria-label={sendLabel}
        title={sendLabel}
        className={styles.chatRound}
      >
        {/* Dictation CONFIRMS a transcript (✓); a voice note SENDS a message, so it wears the
            composer's own send arrow — the same glyph the button it replaced was showing. */}
        {voiceNote ? <SendIcon /> : <ConfirmIcon />}
      </IconButton>
    </>
  );
}
